import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useMyTeam } from '../team/useMyTeam';
import styles from './Sidebar.module.css';

type NavItem =
  | {
      key: string;
      label: string;
      icon: JSX.Element;
      to: string;
      enabled: true;
      end?: boolean;
    }
  | {
      key: string;
      label: string;
      icon: JSX.Element;
      enabled: false;
    };

const NAV_ITEMS: NavItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    enabled: true,
    to: '/dashboard',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="9" rx="1.5" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" />
        <rect x="14" y="12" width="7" height="9" rx="1.5" />
        <rect x="3" y="16" width="7" height="5" rx="1.5" />
      </svg>
    ),
  },
  {
    key: 'strategies',
    label: 'Strategies',
    enabled: true,
    to: '/strategies',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h12l4 4v12H4z" />
        <path d="M16 4v4h4" />
        <path d="M8 13h8M8 17h5" />
      </svg>
    ),
  },
  {
    key: 'learn',
    label: 'Learn',
    enabled: true,
    to: '/learn',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z" />
        <path d="M13 4h5.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H13z" />
      </svg>
    ),
  },
  {
    key: 'live',
    label: 'Live',
    enabled: true,
    to: '/live',
    end: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    key: 'settings',
    label: 'Settings',
    enabled: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1A2 2 0 1 1 7 5l.1.1a1.7 1.7 0 0 0 1.8.3h0a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5h0a1.7 1.7 0 0 0 1.8-.3l.1-.1A2 2 0 1 1 19.6 7l-.1.1a1.7 1.7 0 0 0-.3 1.8v0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
      </svg>
    ),
  },
];

const STUDIO_ITEM: NavItem = {
  key: 'studio',
  label: 'Studio',
  enabled: true,
  to: '/studio',
  icon: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4z" />
    </svg>
  ),
};

const LINEUPS_ITEM: NavItem = {
  key: 'lineups',
  label: 'Lineups',
  enabled: true,
  to: '/lineups',
  end: true,
  icon: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  ),
};

function navItems(role: string | undefined): NavItem[] {
  const strategiesIndex = NAV_ITEMS.findIndex((item) => item.key === 'strategies');
  const extras: NavItem[] = [];
  if (role === 'strategist') extras.push(STUDIO_ITEM);
  if (role === 'strategist' || role === 'player') extras.push(LINEUPS_ITEM);
  return [
    ...NAV_ITEMS.slice(0, strategiesIndex + 1),
    ...extras,
    ...NAV_ITEMS.slice(strategiesIndex + 1),
  ];
}

export default function Sidebar() {
  const { user } = useAuth();
  const { team } = useMyTeam(user?.id);
  const items = navItems(team?.role);

  return (
    <aside className={styles.sidebar}>
      <div className={styles.brand}>
        <div className={styles.brandMark}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3h4l1 3-1 2h-4l-1-2z" />
            <path d="M10 8l-2 8 4 7 4-7-2-8" />
          </svg>
        </div>
        <div className={styles.brandText}>
          <div className={styles.brandCompany}>
            Bring&nbsp;Me
            <br />
            The&nbsp;Pistols
          </div>
          <span className={styles.brandRule} />
          <div className={styles.brandTool}>
            <span className={styles.diamond}>◆</span> Stratos <span className={styles.ver}>v0.1.0</span>
          </div>
        </div>
      </div>

      <div className={styles.navSection}>
        <div className={styles.navLabel}>Playbook</div>
        <nav className={styles.nav}>
          {items.map((item) => {
            if (item.enabled) {
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  className={({ isActive }) =>
                    `${styles.navItem} ${styles.navLink} ${isActive ? styles.active : ''}`
                  }
                  end={item.end}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              );
            }
            return (
              <button key={item.key} type="button" className={styles.navItem} disabled>
                {item.icon}
                <span>{item.label}</span>
                <span className={styles.soonBadge}>Soon</span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className={styles.sysStatus}>
        <span className={styles.sysDot} />
        <span className={styles.sysLabel}>Relay</span>
        <span className={styles.sysValue}>Online</span>
      </div>
    </aside>
  );
}
